import {
    IActuatorInfoResponse,
    PacketId,
    serializeActuatorInfoRequest,
    serializeReadTotalActuatorsRequest,
} from "../utils/common-types";
import { hidRequest, hidRequestMulti } from "./hid";
import { TelemIdxType } from "cstore/telem";
import { SubActuators } from "utils/slnt-file";

const INITIAL_DELAY_MS = 2000; // 2 seconds initial delay before starting polling
const POLL_INTERVAL_MS = 1000; // 1 second poll interval
const ACTUATOR_INFO_PER_RESPONSE_PACKET_TIMEOUT_MS = 20; //ms; keep this a very short interval

export enum SubActuatorStep {
    Warmup,
    OK,
    Error,
}

export type SubActuatorState =
    | {
          step: SubActuatorStep.Warmup;
      }
    | {
          step: SubActuatorStep.OK;
          actuators: SubActuators;
      }
    | {
          step: SubActuatorStep.Error;
          err: GetActuatorInfoError | string;
      };

// Field type lookup for multi-motor telemetry field suffixes.
// Note: torqueActualValue and currentActualValue are int16 on the wire but there
// is no TYPE_INT16, so TYPE_INT32 is correct for JS storage.
export const multiMotorFieldTypes: { [fieldName: string]: TelemIdxType } = {
    faultsBitmapLower32:  "TYPE_UINT32",
    faultsBitmapUpper16:  "TYPE_UINT16",
    currentPosition:      "TYPE_INT32",
    velocityActualValue:  "TYPE_INT32",
    torqueActualValue:    "TYPE_INT32",
    currentActualValue:   "TYPE_INT32",
    targetPosition:       "TYPE_UINT32",
    statusWord:           "TYPE_UINT16",
    mosfetTemperature:    "TYPE_FLOAT",
    batteryCurrent:       "TYPE_FLOAT",
};

// TODO: use a better approach for getting these (i.e., generate this array using the Python code in salient-types)
// These are not PlotKeys, but rather suffixes of PlotKeys.
// Also note that this excludes actuatorNodeId, which is not telemetry.
export const keysOfIMultiMotorTelemetryMessage: string[] = [
    "faultsBitmapLower32",
    "faultsBitmapUpper16",
    "currentPosition",
    "velocityActualValue",
    "torqueActualValue",
    "currentActualValue",
    "targetPosition",
    "statusWord",
    "mosfetTemperature",
    "batteryCurrent",
];

export class GetActuatorInfoError extends Error {
    constructor(m: string) {
        super(m);

        // Set the prototype explicitly.
        Object.setPrototypeOf(this, GetActuatorInfoError.prototype);
    }
}

export const getActuatorInfoFromPSCB = async (
    device: HIDDevice,
): Promise<SubActuators> => {
    const { detectedNodes, expectedNodes, operationalNodesBitmap } =
        await hidRequest(
            device,
            PacketId.READ_TOTAL_ACTUATORS,
            serializeReadTotalActuatorsRequest({}),
        );

    console.log("totalActuators", { detectedNodes, expectedNodes, operationalNodesBitmap }); // ToDo: remove
    let actuators: SubActuators = {};
    if (detectedNodes === 0) return actuators;

    const timeouts = Array.from(
        { length: detectedNodes },
        () => ACTUATOR_INFO_PER_RESPONSE_PACKET_TIMEOUT_MS,
    );
    const promises = hidRequestMulti(
        device,
        PacketId.ACTUATOR_INFO,
        serializeActuatorInfoRequest({}),
        timeouts,
    );
    for (const promise of promises) {
        try {
            const info: IActuatorInfoResponse = await promise;
            if (actuators[info.nodeId]) {
                console.warn(`WARNING: nodeId ${info.nodeId} sent multiple times`);
            }
            const disposition = new TextDecoder().decode(
                info.dispositionStr as Uint8Array,
            );
            actuators[info.nodeId] = {
                nodeId: info.nodeId,
                disposition,
            };
        } catch (e) {
            throw new GetActuatorInfoError(`${e}`);
        }
    }
    return actuators;
};

export type StatusUpdateCb = (state?: SubActuatorState) => void;

const MAX_CONSECUTIVE_FAILED_READ_TOTAL_ACTUATORS = 5;

export class SubActuatorManager {
    state: SubActuatorState;
    statusUpdateCb: StatusUpdateCb | undefined;
    private device: HIDDevice;
    private pollTimer?: ReturnType<typeof setTimeout>;
    private stopped: boolean;
    private consecutiveFailedReadTotalActuators: number;

    constructor(device: HIDDevice, statusUpdateCb?: StatusUpdateCb) {
        this.device = device;
        this.state = { step: SubActuatorStep.Warmup };
        this.statusUpdateCb = statusUpdateCb;
        this.stopped = false;
        this.consecutiveFailedReadTotalActuators = 0;
    }

    async run() {
        // Initial delay before starting polling
        await new Promise((resolve) => setTimeout(resolve, INITIAL_DELAY_MS));

        if (this.stopped) return;

        // Start polling
        this.poll();
    }

    stop() {
        this.stopped = true;
        if (this.pollTimer !== undefined) {
            clearTimeout(this.pollTimer);
            this.pollTimer = undefined;
        }
    }

    actuators(): SubActuators {
        if (this.state.step === SubActuatorStep.OK) {
            return this.state.actuators;
        }
        return {};
    }

    private async poll() {
        if (this.stopped) return;

        let failedReadTotalActuators = false;
        try {
            const actuators = await getActuatorInfoFromPSCB(this.device);
            this.state = {
                step: SubActuatorStep.OK,
                actuators,
            };
            this.statusUpdateCb?.(this.state);
        } catch (e) {
            let err: GetActuatorInfoError | string;
            failedReadTotalActuators = true;
            this.consecutiveFailedReadTotalActuators++;
            if (e instanceof GetActuatorInfoError) {
                err = e;
            } else {
                err = `${e}`;
            }

            this.state = {
                step: SubActuatorStep.Error,
                err,
            };
            this.statusUpdateCb?.(this.state);
        }

        if ( !failedReadTotalActuators ) this.consecutiveFailedReadTotalActuators = 0;
        else if ( this.consecutiveFailedReadTotalActuators > MAX_CONSECUTIVE_FAILED_READ_TOTAL_ACTUATORS ) {
            console.info( "Stopping SubActuatorManager since device may be disconnected or not a PSCB" );
            this.stop();
        }

        // Schedule next poll
        if (!this.stopped) {
            this.pollTimer = setTimeout(() => this.poll(), POLL_INTERVAL_MS);
        }
    }
}
